import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {RouterModule} from '@angular/router';
import {CoreModule} from '@catcore/core.module';

import {ReactiveFormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ResourceActions} from '@catfeatures/actions/resource.actions';
import {FeatureDetailsComponent} from '@catfeatures/components/feature-details/feature-details.component';
import {FeatureDiagramComponent} from '@catfeatures/components/feature-diagram/feature-diagram.component';
import {FeatureListComponent} from '@catfeatures/components/feature-list/feature-list.component';
import {FeaturePropertiesComponent} from '@catfeatures/components/feature-properties/feature-properties.component';
import {FeatureToProjectComponent} from '@catfeatures/components/feature-to-project/feature-to-project.component';
import {FeatureWelcomeComponent} from '@catfeatures/components/feature-welcome/feature-welcome.component';
import {FeaturesComponent} from '@catfeatures/components/features/features.component';
import {FeatureFixedRoutesLandingComponent} from '@catfeatures/components/landing-pages/feature-fixed-routes-landing/feature-fixed-routes-landing.component';
import {FeatureGgwDevicesLandingComponent} from '@catfeatures/components/landing-pages/feature-ggw-devices-landing/feature-ggw-devices-landing.component';
import {FeaturePortsLandingComponent} from '@catfeatures/components/landing-pages/feature-ports-landing/feature-ports-landing.component';
import {FeatureServicesWelcomeComponent} from '@catfeatures/components/landing-pages/feature-services-welcome/feature-services-welcome.component';
import {FeatureUnitsWelcomeComponent} from '@catfeatures/components/landing-pages/feature-units-welcome/feature-units-welcome.component';
import {ResourceEffects} from '@catfeatures/effects/resource.effects';
import {resourceReducer} from '@catfeatures/reducers/resource.reducer';
import {FeatureGgwDevicesResolve} from '@catfeatures/services/feature-ggw-devices.resolve';
import {FeatureGgwFixedRoutesResolve} from '@catfeatures/services/feature-ggw-fixed-routes.resolve';
import {FeatureGgwImplementationsResolve} from '@catfeatures/services/feature-ggw-implementations.resolve';
import {FeatureGroupTypeResolve} from '@catfeatures/services/feature-group-type.resolve';
import {FeatureServicesResolve} from '@catfeatures/services/feature-services.resolve';
import {FeatureResolve} from '@catfeatures/services/feature.resolve';
import {FeaturesResolve} from '@catfeatures/services/features.resolve';
import {GenericResourceApiService} from '@catfeatures/services/generic-resource-api.service';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {StatusesResolve} from '@catfeatures/services/statuses.resolve';
import {UnitsResolve} from '@catfeatures/services/units.resolve';
import {EvidFunctionModule} from '@catmodules/models/evid-functions/evid-function.module';
import {FeatureEvidFunctionModule} from '@catmodules/models/feature-evid-functions/feature-evid-function.module';
import {FeatureGgwImplementationsModule} from '@catmodules/models/feature-ggw-implementations/feature-ggw-implementations.module';
import {FeaturePortModule} from '@catmodules/models/feature-port/feature-port.module';
import {FeatureServicesModule} from '@catmodules/models/feature-services/feature-services.module';
import {FeatureUnitsModule} from '@catmodules/models/feature-units/feature-units.module';
import {GgwDeviceParamsModule} from '@catmodules/models/ggw-device-params/ggw-device-params.module';
import {GgwImplementationsModule} from '@catmodules/models/ggw-implementations/ggw-implementations.module';
import {ServicesModule} from '@catmodules/models/services/services.module';
import {BreadcrumbModule} from '@catmodules/widgets/breadcrumb/breadcrumb.module';
import {ExplorerModule} from '@catmodules/widgets/explorer/explorer.module';
import {SlideInModalModule} from '@catmodules/widgets/slide-in-modal/slide-in-modal.module';
import {UIBaseModule} from '@catui/ui-base.module';
import {UIFormModule} from '@catuiform/ui-form.module';
import {EffectsModule} from '@ngrx/effects';
import {StoreModule} from '@ngrx/store';
import {ConfirmationService, ConfirmDialogModule, DropdownModule, TreeTableModule} from 'primeng/primeng';
import { FeatureOptionTokensWelcomeComponent } from './components/landing-pages/feature-option-tokens-welcome/feature-option-tokens-welcome.component';
import {FeatureOptionTokenModule} from "@catmodels/feature-option-tokens/feature-option-token.module";
import {GgwFixedRoutesModule} from "@catmodels/ggw-fixed-routes/ggw-fixed-routes.module";
import {FeatureParameterModule} from "@catmodels/feature-parameter/feature-parameter.module";
import {FeatureAvodCatalogModule} from "@catmodels/avod-catalog/feature-avod-catalog.module";
import {FeatureProjectUsagesComponent} from "@catfeatures/components/project-usages/feature-project-usages.component";
import {NgxGraphModule} from "@swimlane/ngx-graph";
import {TooltipModule} from "@swimlane/ngx-charts";
import {FeatureDynamicRoutesLandingComponent} from "@catfeatures/components/landing-pages/feature-dynamic-routes-landing/feature-dynamic-routes-landing.component";

const publicComponents = [
  FeaturesComponent,
  FeatureListComponent,
  FeatureWelcomeComponent,
  FeatureDetailsComponent,
  FeaturePropertiesComponent,
  FeatureDiagramComponent,
  FeatureUnitsWelcomeComponent,
  FeatureServicesWelcomeComponent,
  FeatureGgwDevicesLandingComponent,
  FeatureFixedRoutesLandingComponent,
  FeatureDynamicRoutesLandingComponent,
  FeaturePortsLandingComponent,
  FeatureOptionTokensWelcomeComponent,
  FeatureProjectUsagesComponent,
];

const imports = [
  // common
  CommonModule,
  RouterModule,
  ReactiveFormsModule,
  DropdownModule,
  BrowserAnimationsModule,
  TreeTableModule,
  NgxGraphModule,
  TooltipModule,


  // app
  CoreModule,
  UIBaseModule,
  UIFormModule,
  EvidFunctionModule,
  FeatureServicesModule,
  FeatureParameterModule,
  FeatureEvidFunctionModule,
  FeatureUnitsModule,
  FeaturePortModule,
  ServicesModule,
  GgwImplementationsModule,
  GgwFixedRoutesModule,
  GgwDeviceParamsModule,
  FeatureGgwImplementationsModule,
  ExplorerModule,
  BreadcrumbModule,
  ConfirmDialogModule,
  FeatureOptionTokenModule,
  SlideInModalModule,
  FeatureAvodCatalogModule,

  StoreModule.forFeature(
    'features',
    {
      resource: resourceReducer,
    }),

  EffectsModule.forFeature([ResourceEffects]),

];

const providers = [
  StatusesResolve,
  FeatureGroupTypeResolve,
  FeatureResolve,
  FeaturesResolve,
  UnitsResolve,
  GenericResourceService,
  GenericResourceApiService,
  FeatureServicesResolve,
  FeatureGgwImplementationsResolve,
  FeatureGgwDevicesResolve,
  FeatureGgwFixedRoutesResolve,
  ResourceActions,
  ConfirmationService,
];

const declarations = [
  FeatureToProjectComponent,
  ...publicComponents,
];
const exportings = [
  ...publicComponents,
];

@NgModule({
  entryComponents: [FeatureToProjectComponent],
  imports,
  declarations,
  providers,
  exports: exportings,
})
export class FeaturesModule {
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/features/features.module.ts